import React from 'react';
import { useIntl } from 'react-intl';

import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';

import { DialogWizardBaseProps, DialogWizardScreen } from './dialog-wizard.types';
import { DialogWizardLoadingScreen } from './dialog-wizard-loading-screen';
import { DialogWizardSuccessScreen } from './dialog-wizard-success-screen';

/**
 * Internal base component that renders the Dialog shell and switches
 * between CONFIRM/ERROR (children), LOADING, and SUCCESS screens.
 */
export const DialogWizardBase = ({
  ariaLabel,
  isOpen,
  onClose,
  screen,
  title,
  subtitle,
  loadingMessage,
  successMessage,
  closeButtonText,
  modalSize = 'medium',
  dataTestId = 'dialog-wizard',
  greyBackground = true,
  children
}: DialogWizardBaseProps) => {
  const intl = useIntl();

  const defaultCloseText = intl.formatMessage({
    id: 'common.close',
    defaultMessage: 'Close'
  });

  const renderContent = () => {
    switch (screen) {
      case DialogWizardScreen.LOADING:
        return <DialogWizardLoadingScreen text={loadingMessage} />;

      case DialogWizardScreen.SUCCESS:
        return (
          <Form
            onSubmit={() => null}
            saveButton={
              <Button onClick={onClose} buttonType="primary" data-testid={`${dataTestId}-close-button`}>
                {closeButtonText || defaultCloseText}
              </Button>
            }
            stickyFooter
          >
            <DialogWizardSuccessScreen text={successMessage} />
          </Form>
        );

      case DialogWizardScreen.CONFIRM:
      case DialogWizardScreen.ERROR:
      default:
        return children;
    }
  };

  return (
    <Dialog
      aria-label={ariaLabel}
      open={isOpen}
      onCancel={onClose}
      size={modalSize}
      title={title}
      subtitle={subtitle}
      data-testid={dataTestId}
      greyBackground={greyBackground}
    >
      {renderContent()}
    </Dialog>
  );
};
